/**
 * OAuth2 Utilities for TPOS Account (account.tpos.dev)
 *
 * Implements OAuth2 Authorization Code Flow with PKCE (RFC 7636)
 * for integration with account.tpos.dev provider
 */

import axios from "axios";
import type { OAuth2Provider } from "@/types/oauth2-provider";

/**
 * Get API Base URL
 * Priority:
 * 1. Runtime config from window.APP_CONFIG (Kubernetes ConfigMap in production)
 * 2. Environment variable VITE_API_BASE_URL (build-time configuration)
 * 3. Empty string for relative URLs (development with Vite proxy)
 */
const getApiBaseUrl = (): string => {
  // Production: Use runtime config from Kubernetes ConfigMap
  if (window.APP_CONFIG?.apiUrl) {
    return window.APP_CONFIG.apiUrl;
  }

  // Build-time: Use environment variable if set
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl) {
    return envUrl;
  }

  // Development: Use relative URL (Vite proxy handles routing to localhost:5100)
  return "";
};

const API_BASE_URL = getApiBaseUrl();

// Create dedicated axios client for OAuth2 (not using api wrapper)
const oauth2Client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * OAuth2 providers that exist only for local development.
 *
 * `localhost-oauth` (displayed as "Account.Tpos.dev") is a developer sign-in route: it is
 * configured on dev and staging and switched off in production, so every deployed environment
 * either offers a login nobody outside the team should use or a button that cannot work.
 * It stays on the page only when the console is being served from a developer's own machine.
 */
const LOCAL_ONLY_OAUTH_PROVIDERS = new Set(["localhost-oauth"]);

/** Hostnames that mean "this console is running on the developer's own machine". */
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * Decides whether a provider returned by the backend should be offered on the sign-in screen.
 *
 * Keyed on the HOSTNAME, not on `import.meta.env.DEV`: a development build served from a
 * cluster (dev/staging run exactly that) is still a deployed environment, so a build-mode check
 * would keep the button visible in the two places this is meant to remove it from.
 */
export const isOAuthProviderVisible = (providerName: string): boolean =>
  !LOCAL_ONLY_OAUTH_PROVIDERS.has(providerName) ||
  LOCAL_HOSTNAMES.has(window.location.hostname);

/**
 * OAuth2 Provider Configuration
 */
export const OAUTH2_PROVIDER = "wiaccount"; // Default OAuth2 provider
export const OAUTH2_SESSION_KEY = "wicloud_oauth2_session";
export const SESSION_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

/**
 * OAuth2 Request/Response Types
 */
export interface OAuth2InitiateRequest {
  provider: string;
  redirectUri: string;
}

export interface OAuth2InitiateResponse {
  authorizationUrl: string;
  state: string;
  codeVerifier: string;
}

export interface OAuth2CallbackRequest {
  provider: string;
  code: string;
  state: string;
  codeVerifier: string;
}

export interface OAuth2CallbackResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    isActive: boolean;
  };
}

export interface OAuth2SessionData {
  state: string;
  codeVerifier: string;
  timestamp: number;
  redirectUri: string;
  provider: string;
}

/**
 * PKCE Utilities (RFC 7636)
 */
const base64URLEncode = (buffer: ArrayBuffer): string => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array.buffer);
};

export const generateCodeChallenge = async (
  verifier: string,
): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64URLEncode(digest);
};

export const isCryptoSupported = (): boolean => {
  return !!(
    window.crypto &&
    window.crypto.subtle &&
    window.crypto.getRandomValues
  );
};

export const validateState = (
  receivedState: string,
  storedState: string,
): boolean => {
  return receivedState === storedState;
};

/**
 * OAuth2 API Service
 */
/**
 * Standard API Response Wrapper from Backend
 */
interface StandardResponse<T> {
  success: boolean;
  error: null | {
    code: string;
    message: string;
    details?: unknown;
  };
  data: T;
}

export const oauth2Service = {
  /**
   * Initiate OAuth2 flow - gets authorization URL from backend
   */
  async initiate(data: OAuth2InitiateRequest): Promise<OAuth2InitiateResponse> {
    try {
      const response = await oauth2Client.post<
        StandardResponse<OAuth2InitiateResponse>
      >("/api/v1/oauth2/initiate", data);

      // Check for error response from backend
      if (!response.data.success && response.data.error) {
        const errorMessage =
          response.data.error.message || "Failed to initiate OAuth flow";
        throw new Error(errorMessage);
      }

      // Backend wraps response in StandardResponse format
      return response.data.data;
    } catch (error) {
      // Handle axios errors and extract backend error message
      if (axios.isAxiosError(error) && error.response?.data) {
        const errorData = error.response.data as StandardResponse<unknown>;

        // Extract error message from StandardResponse format
        if (!errorData.success && errorData.error) {
          const errorMessage =
            errorData.error.message || "Failed to initiate OAuth flow";
          throw new Error(errorMessage);
        }
      }

      // Re-throw original error if it's already an Error
      if (error instanceof Error) {
        throw error;
      }

      // Fallback for unknown errors
      throw new Error("Failed to initiate OAuth flow. Please try again.");
    }
  },

  /**
   * Handle OAuth2 callback - exchange authorization code for tokens
   */
  async callback(data: OAuth2CallbackRequest): Promise<OAuth2CallbackResponse> {
    try {
      const response = await oauth2Client.post<
        StandardResponse<OAuth2CallbackResponse>
      >("/api/v1/oauth2/callback", {
        provider: data.provider,
        code: data.code,
        state: data.state,
        codeVerifier: data.codeVerifier,
      });

      // Check for error response from backend
      if (!response.data.success && response.data.error) {
        const errorMessage =
          response.data.error.message || "OAuth authentication failed";
        throw new Error(errorMessage);
      }

      // Backend wraps response in StandardResponse format
      return response.data.data;
    } catch (error) {
      // Handle axios errors and extract backend error message
      if (axios.isAxiosError(error) && error.response?.data) {
        const errorData = error.response.data as StandardResponse<unknown>;

        // Extract error message from StandardResponse format
        if (!errorData.success && errorData.error) {
          const errorMessage =
            errorData.error.message || "OAuth authentication failed";
          throw new Error(errorMessage);
        }

        // Fallback to axios error message
        if (errorData.error?.message) {
          throw new Error(errorData.error.message);
        }
      }

      // Re-throw original error if it's already an Error
      if (error instanceof Error) {
        throw error;
      }

      // Fallback for unknown errors
      throw new Error("OAuth authentication failed. Please try again.");
    }
  },

  /**
   * Get available OAuth2 providers
   */
  async getProviders(): Promise<OAuth2Provider[]> {
    const response = await oauth2Client.get<StandardResponse<OAuth2Provider[]>>(
      "/api/v1/oauth2/providers",
    );
    return response.data.data;
  },
};

/**
 * Session Storage Utilities
 */
export const oauth2Session = {
  save(data: OAuth2SessionData): void {
    localStorage.setItem(OAUTH2_SESSION_KEY, JSON.stringify(data));
  },

  get(): OAuth2SessionData | null {
    const data = localStorage.getItem(OAUTH2_SESSION_KEY);
    return data ? JSON.parse(data) : null;
  },

  clear(): void {
    localStorage.removeItem(OAUTH2_SESSION_KEY);
  },

  isExpired(session: OAuth2SessionData): boolean {
    return Date.now() - session.timestamp > SESSION_EXPIRY_MS;
  },
};

/**
 * DEPRECATED: Use useOAuth2Flow hook instead
 * This function is kept for backward compatibility but should not be used in new code.
 * @deprecated Use the useOAuth2Flow hook from @/hooks/useOAuth2Flow
 */
export const initiateTPOSOAuth2 = async (): Promise<void> => {
  throw new Error("Deprecated: Use useOAuth2Flow hook instead");
};

/**
 * DEPRECATED: Use useOAuth2Callback hook instead
 * This function is kept for backward compatibility but should not be used in new code.
 * @deprecated Use the useOAuth2Callback hook from @/hooks/useOAuth2Callback
 */
export const handleTPOSOAuth2Callback = async (
  _code: string,
  _state: string,
): Promise<OAuth2CallbackResponse> => {
  throw new Error("Deprecated: Use useOAuth2Callback hook instead");
};
